import type React from "react";
import { useState } from "react";
import { MediaPreviewDialog } from "@/components/common/media-preview/MediaPreviewDialog";
import { SingleMediaPreview } from "@/components/common/media-preview/SingleMediaPreview";
import type { MediaFile } from "@/components/common/media-preview/types";
import {
  getMediaKind,
  TILE_GRID_GAP_CLASS,
  type TileLayoutMode,
} from "@/components/common/media-preview/utils";
import { triggerBrowserDownload } from "@/lib/utils/mediaFile";
import { cn } from "@/lib/utils";

interface MediaPreviewProps {
  files: MediaFile[];
  className?: string;
  layoutVariant?: "content" | "full";
  variant?: "default" | "chat";
  singleTileWidth?: "full" | "fixed" | "fit";
  tileGap?: "default" | "none";
}

const resolveTileLayoutMode = ({
  shouldUseGrid,
  shouldUseTileMode,
  singleTileWidth,
}: {
  shouldUseGrid: boolean;
  shouldUseTileMode: boolean;
  singleTileWidth: NonNullable<MediaPreviewProps["singleTileWidth"]>;
}): TileLayoutMode => {
  if (shouldUseGrid) return "fill-column";
  if (!shouldUseTileMode) return "fit-content";

  if (singleTileWidth === "full") return "fill-column";
  if (singleTileWidth === "fit") return "fit-content";
  return "fixed-width";
};

export const MediaPreview: React.FC<MediaPreviewProps> = ({
  files,
  className = "",
  layoutVariant = "content",
  variant = "default",
  singleTileWidth,
  tileGap,
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (!files || files.length === 0) {
    return null;
  }

  const resolvedSingleTileWidth =
    singleTileWidth ?? (variant === "chat" ? "full" : "fixed");
  const resolvedTileGap = tileGap ?? (variant === "chat" ? "none" : "default");

  const shouldUseGrid = files.length >= 2;
  const isSingleNonImageFile =
    files.length === 1 && getMediaKind(files[0]) !== "image";
  const shouldUseTileMode = shouldUseGrid || isSingleNonImageFile;
  const tileLayoutMode = resolveTileLayoutMode({
    shouldUseGrid,
    shouldUseTileMode,
    singleTileWidth: resolvedSingleTileWidth,
  });
  const twoColumnLayoutClass =
    layoutVariant === "full"
      ? "grid-cols-2"
      : "grid-cols-[repeat(2,minmax(0,1fr))]";
  const tileGapClass =
    resolvedTileGap === "none" ? "gap-0" : TILE_GRID_GAP_CLASS;
  const containerClass = shouldUseGrid
    ? `grid ${twoColumnLayoutClass} ${tileGapClass}`
    : isSingleNonImageFile
      ? "grid grid-cols-1 gap-2"
      : "space-y-3";

  const handleOpenPreview = (index: number) => {
    const file = files[index];
    if (!file) return;

    const mediaKind = getMediaKind(file);
    if (mediaKind === "file") {
      triggerBrowserDownload({ href: file.url, fileName: file.file_name });
      return;
    }

    setSelectedIndex(index);
  };

  return (
    <>
      <div className={cn(containerClass, className)}>
        {files.map((file, index) => (
          <SingleMediaPreview
            key={file.id}
            file={file}
            index={index}
            onOpenPreview={handleOpenPreview}
            mode={shouldUseTileMode ? "tile" : "full"}
            layoutMode={tileLayoutMode}
          />
        ))}
      </div>

      <MediaPreviewDialog
        files={files}
        selectedIndex={selectedIndex}
        onSelectedIndexChange={setSelectedIndex}
      />
    </>
  );
};
